import { coordEq, MAX_HP, unitTotalHp } from "@dailywars/core/engine";
import type { Coord } from "@dailywars/core/engine";
import { Link } from "@tanstack/react-router";

import type { StoreState } from "#/game/store/types";

import { COORDS, enemyUnit, isIdle, isUnitSelected, ownUnitAt } from "./tutorialStepHelpers";
import type { TutorialStep } from "./tutorialStepTypes";

const { factory, sniperBarracks, sniperAttackFrom, sniperTooFar, tankAttackFrom, enemyBarracks } =
  COORDS;

export const selectLightTank: TutorialStep = {
  id: "select-light-tank",
  buildKind: null,
  enableEndTurn: false,
  prompt: () => ({
    title: "Select your Light Tank",
    body: "Tap your Light Tank. Direct units counter-attack when they survive.",
    highlight: { kind: "tile", coord: factory },
  }),
  allowsTile: (_store, tile) => coordEq(tile, factory),
  isComplete: (store) => isUnitSelected(store, { kind: "lightTank", coord: factory }),
};

export const attackWithLightTank: TutorialStep = {
  id: "attack-with-light-tank",
  buildKind: null,
  enableEndTurn: false,
  prompt: (mode) => {
    if (mode === "move-preview" || mode === "confirm-attack") {
      return {
        title: "Attack!",
        body: "Attack the enemy RPG. Direct units counter-attack when they survive, and RPGs hit armor hard.",
        highlight: { kind: "tile", coord: enemyBarracks },
      };
    }
    return {
      title: "Move in to attack",
      body: "Move your Light Tank onto the highlighted tile, right beside the enemy.",
      highlight: { kind: "tile", coord: tankAttackFrom },
    };
  },
  allowsTile: (store, tile: Coord) => {
    const mode = store.selection.mode;
    if (mode === "unit") return coordEq(tile, tankAttackFrom);
    if (mode === "move-preview") return coordEq(tile, enemyBarracks);
    if (mode === "confirm-attack") return true;
    if (mode === "none") return coordEq(tile, factory);
    return false;
  },
  isComplete: (store) => {
    const enemy = enemyUnit(store.localState);
    return Boolean(enemy) && unitTotalHp(enemy!) < MAX_HP && isIdle(store);
  },
};

export const selectSniper: TutorialStep = {
  id: "select-sniper",
  buildKind: null,
  enableEndTurn: false,
  prompt: () => ({
    title: "Select your Sniper",
    body: "Tap your Sniper. Indirect units strike from range and never take or deal counterattacks.",
    highlight: { kind: "tile", coord: sniperBarracks },
  }),
  allowsTile: (_store, tile) => coordEq(tile, sniperBarracks),
  isComplete: (store) => isUnitSelected(store, { kind: "sniper", coord: sniperBarracks }),
};

export const advanceSniperTooFar: TutorialStep = {
  id: "advance-sniper-too-far",
  buildKind: null,
  enableEndTurn: false,
  prompt: (mode) => {
    if (mode === "move-preview") {
      return {
        title: "Confirm move",
        body: "Tap your moved unit to confirm the move.",
        highlight: { kind: "tile", coord: sniperTooFar },
      };
    }
    return {
      title: "Advance your Sniper",
      body: "Move your Sniper onto the highlighted tile.",
      highlight: { kind: "tile", coord: sniperTooFar },
    };
  },
  allowsTile: (store, tile) => {
    const mode = store.selection.mode;
    if (mode === "unit" || mode === "move-preview") return coordEq(tile, sniperTooFar);
    if (mode === "none") return coordEq(tile, sniperBarracks);
    return false;
  },
  isComplete: (store) =>
    ownUnitAt(store.localState, { coord: sniperTooFar, kind: "sniper" }) && isIdle(store),
};

export const outOfRangeCoach: TutorialStep = {
  id: "out-of-range-coach",
  dismissible: true,
  buildKind: null,
  enableEndTurn: false,
  prompt: () => ({
    title: "Out of range",
    body: "Oops: your Sniper is too far away to attack. If you ever make a wrong move, tap Undo to take it back.",
    highlight: null,
    dismissible: true,
  }),
  allowsTile: () => false,
  isComplete: () => false,
};

export const undoWrongMove: TutorialStep = {
  id: "undo-wrong-move",
  buildKind: null,
  enableEndTurn: false,
  enableUndo: true,
  prompt: () => ({
    title: "Undo your move",
    body: "Tap Undo to return your Sniper to its previous tile.",
    highlight: { kind: "undo" },
  }),
  allowsTile: () => false,
  isComplete: (store) =>
    ownUnitAt(store.localState, { coord: sniperBarracks, kind: "sniper" }) && isIdle(store),
};

export const snipeFromRange: TutorialStep = {
  id: "snipe",
  buildKind: null,
  enableEndTurn: false,
  prompt: (mode) => {
    if (mode === "unit") {
      return {
        title: "Move into firing range",
        body: "Move your Sniper onto the highlighted tile, exactly 2 tiles from the enemy.",
        highlight: { kind: "tile", coord: sniperAttackFrom },
      };
    }
    if (mode === "move-preview" || mode === "confirm-attack") {
      return {
        title: "Finish it off",
        body: "Destroy the weakened enemy. The Sniper strikes from afar and takes no counterattack.",
        highlight: { kind: "tile", coord: enemyBarracks },
      };
    }
    return {
      title: "Select your Sniper",
      body: "Tap your Sniper to select it.",
      highlight: { kind: "tile", coord: sniperBarracks },
    };
  },
  allowsTile: (store, tile: Coord) => {
    const mode = store.selection.mode;
    if (mode === "unit") return coordEq(tile, sniperAttackFrom);
    if (mode === "move-preview") return coordEq(tile, enemyBarracks);
    if (mode === "confirm-attack") return true;
    return coordEq(tile, sniperBarracks);
  },
  isComplete: (store: StoreState) => !enemyUnit(store.localState) && isIdle(store),
};

export const victory: TutorialStep = {
  id: "done",
  buildKind: null,
  enableEndTurn: false,
  isFinal: true,
  prompt: () => ({
    title: "Victory!",
    body: (
      <>
        You learned how build units, capture properties, and use direct and indirect units to
        destroy the enemy. You are now ready to conquer the daily map. For more information on units
        and rules, check the{" "}
        <Link
          to="/codex"
          preload={false}
          className="font-medium text-primary underline underline-offset-4 hover:text-foreground"
        >
          guide
        </Link>
        .
      </>
    ),
    highlight: null,
  }),
  allowsTile: () => false,
  isComplete: () => false,
};
